import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Dialog, Slide } from '@mui/material';
import { Icon } from '@iconify/react';
import { getOccupantDetails } from '../../../../../redux/actions/SocietyPortal/OccupantAction/OccupantAction';
import { formatDate, TENANT_DOCUMENT_SLOTS, absoluteFileUrl } from './tenancyStatus';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const STATUS_STYLES = {
    A: { label: 'Active', className: 'tw-bg-emerald-50 tw-text-emerald-600' },
    I: { label: 'Inactive', className: 'tw-bg-red-50 tw-text-red-500' },
};

const ViewOwnerModal = ({ open, handleClose, member, type = 'owner' }) => {
    const dispatch = useDispatch();
    const { occupantDetails, detailsLoading } = useSelector(state => state.OccupantReducer);
    const [activeTab, setActiveTab] = useState('details');

    // Role-aware fetch: owner rows are FlatOwnership (keyed by
    // flat_ownership_id) while tenant / family are Occupant rows keyed by
    // occupant_id. Same modal handles all three because the fields overlap
    // — the small role-specific bits are branched inline below.
    const isOwner = type === 'owner';
    const memberId = isOwner
        ? (member?.flat_ownership_id || member?.occupant_id)
        : member?.occupant_id;

    useEffect(() => {
        if (member && open && memberId) {
            dispatch(getOccupantDetails({ id: memberId, type }));
            setActiveTab('details');
        }
    }, [member, open, dispatch, memberId, type]);

    const details = occupantDetails || {};
    const entity = details.entity || details;
    // Fall back to the list-row `member.flat` when the details fetch didn't
    // include the association (older payload shapes / partial responses).
    // Ensures the header + Unit Details still populate for family and tenant
    // even if the BE round-trip omits the nested flat.
    const flat = details.flat || member?.flat || {};
    const wing = flat.wing || {};
    const society = wing.society || {};
    const glAccounts = entity.glAccounts || [];

    const isCompany = entity.entity_type === 'C';
    const fullName = isCompany
        ? (entity.company_name || '-')
        : ([entity.first_name, entity.last_name].filter(Boolean).join(' ') || '-');
    const avatarInitial = (isCompany ? entity.company_name : entity.first_name) || '?';
    const flatLabel = wing.title && flat.flat_number ? `${wing.title}-${flat.flat_number}` : flat.flat_number || '-';

    // Status: owners by their ownership row's is_active. Tenants / family have no
    // is_active column - they follow the tenancy rule the list uses (active
    // until the end date has passed), from the details or else the list row.
    // Same rule as the list: active until the end date has passed. Derived here
    // too, so the status is right even against an older API response.
    const endDate = details.end_date || member?.end_date;
    const derivedStatus = !endDate || String(endDate).slice(0, 10) >= new Date().toISOString().slice(0, 10)
        ? 'ACTIVE' : 'INACTIVE';
    const tenancyStatus = details.tenancy_status || member?.tenancy_status || (isOwner ? null : derivedStatus);
    const isLeaving = details.tenancy_status ? details.is_leaving
        : member?.tenancy_status ? member.is_leaving
        : Boolean(endDate) && derivedStatus === 'ACTIVE';
    const status = isOwner
        ? { active: !!details.is_active, leaving: false, label: details.is_active ? 'Active' : 'Inactive' }
        : tenancyStatus
            ? {
                active: tenancyStatus === 'ACTIVE',
                leaving: tenancyStatus === 'ACTIVE' && !!isLeaving,
                label: tenancyStatus !== 'ACTIVE' ? 'Inactive'
                    : isLeaving ? `Leaving ${formatDate(details.end_date || member?.end_date)}` : 'Active'
            }
            : { active: !!details.is_active, leaving: false, label: details.is_active ? 'Active' : 'Inactive' };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth="lg"
            fullWidth
            PaperProps={{ sx: { borderRadius: '12px', overflow: 'hidden', height: '85vh' } }}
        >
            <div className="tw-bg-white tw-flex tw-flex-col tw-h-full">
                {/* Header */}
                <div className="tw-px-5 tw-py-3 tw-border-b tw-border-gray-200 tw-flex tw-items-center tw-justify-between tw-flex-shrink-0">
                    <div>
                        <h3 className="tw-text-sm tw-font-bold tw-text-gray-800">
                            {isOwner ? 'Owner' : type === 'tenant' ? 'Tenant' : 'Family'} Details — Flat {flatLabel}
                        </h3>
                        <p className="tw-text-[10px] tw-text-gray-400">{society.name || 'Society Management'}</p>
                    </div>
                    <button
                        onClick={handleClose}
                        className="tw-w-7 tw-h-7 tw-rounded-md tw-text-gray-400 hover:tw-text-gray-600 hover:tw-bg-gray-100 tw-flex tw-items-center tw-justify-center tw-transition-colors"
                    >
                        <Icon icon="mdi:close" className="tw-text-lg" />
                    </button>
                </div>

                {detailsLoading ? (
                    <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-16">
                        <div className="tw-animate-spin tw-rounded-full tw-h-7 tw-w-7 tw-border-2 tw-border-blue-600 tw-border-t-transparent"></div>
                        <p className="tw-text-xs tw-text-gray-500 tw-mt-2">Loading details...</p>
                    </div>
                ) : (
                    <div className="tw-overflow-y-auto tw-flex-1">
                        {/* Profile Section */}
                        <div className="tw-px-5 tw-py-4 tw-flex tw-items-start tw-gap-5">
                            {/* Avatar + Name */}
                            <div className="tw-flex tw-items-center tw-gap-3 tw-flex-shrink-0">
                                <div className="tw-w-12 tw-h-12 tw-rounded-full tw-bg-blue-100 tw-flex tw-items-center tw-justify-center tw-text-blue-600 tw-text-lg tw-font-bold">
                                    {avatarInitial[0]?.toUpperCase()}
                                </div>
                                <div>
                                    <p className="tw-text-sm tw-font-bold tw-text-gray-800">{fullName}</p>
                                    <p className="tw-text-[11px] tw-text-gray-400">
                                        {isOwner ? 'Owner' : type === 'tenant' ? 'Tenant' : 'Family Member'} of Flat {flatLabel}
                                    </p>
                                    <p className="tw-text-[10px] tw-text-gray-400">
                                        ID: #{details.flat_ownership_id || details.occupant_id || '-'}
                                    </p>
                                </div>
                            </div>

                            {/* Info Cards */}
                            <div className="tw-flex-1 tw-flex tw-items-center tw-gap-3">
                                <div className="tw-flex tw-items-center tw-gap-2.5 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2 tw-flex-1">
                                    <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-blue-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                        <Icon icon="mdi:phone-outline" className="tw-text-blue-500 tw-text-sm" />
                                    </div>
                                    <div className="tw-min-w-0">
                                        <p className="tw-text-[9px] tw-text-gray-600 tw-leading-tight">Phone</p>
                                        <p className="tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-truncate">{entity.phone || '-'}</p>
                                    </div>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-2.5 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2 tw-flex-1">
                                    <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-purple-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                        <Icon icon="mdi:email-outline" className="tw-text-purple-500 tw-text-sm" />
                                    </div>
                                    <div className="tw-min-w-0">
                                        <p className="tw-text-[9px] tw-text-gray-600 tw-leading-tight">Email</p>
                                        <p className="tw-text-[11px] tw-font-semibold tw-text-gray-700 tw-truncate">{entity.email || '-'}</p>
                                    </div>
                                </div>
                                <div className="tw-flex tw-items-center tw-gap-2.5 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2 tw-flex-1">
                                    <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-orange-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                        <Icon icon="mdi:home-city-outline" className="tw-text-orange-500 tw-text-sm" />
                                    </div>
                                    <div className="tw-min-w-0">
                                        <p className="tw-text-[9px] tw-text-gray-600 tw-leading-tight">Flat Type</p>
                                        <p className="tw-text-[11px] tw-font-semibold tw-text-gray-700">{flat.flat_type || '-'}</p>
                                    </div>
                                </div>
                                {/* Share is an ownership figure - tenants / family show Resident instead. */}
                                {isOwner ? (
                                    <div className="tw-flex tw-items-center tw-gap-2.5 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2 tw-flex-1">
                                        <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-emerald-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                            <Icon icon="mdi:percent-outline" className="tw-text-emerald-500 tw-text-sm" />
                                        </div>
                                        <div className="tw-min-w-0">
                                            <p className="tw-text-[9px] tw-text-gray-600 tw-leading-tight">Share</p>
                                            <p className="tw-text-[11px] tw-font-semibold tw-text-gray-700">{details.ownership_share ? `${details.ownership_share}%` : '-'}</p>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="tw-flex tw-items-center tw-gap-2.5 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2 tw-flex-1">
                                        <div className="tw-w-7 tw-h-7 tw-rounded-md tw-bg-emerald-50 tw-flex tw-items-center tw-justify-center tw-flex-shrink-0">
                                            <Icon icon="mdi:home-account" className="tw-text-emerald-500 tw-text-sm" />
                                        </div>
                                        <div className="tw-min-w-0">
                                            <p className="tw-text-[9px] tw-text-gray-600 tw-leading-tight">Resident</p>
                                            <p className="tw-text-[11px] tw-font-semibold tw-text-gray-700">{details.is_resident ? 'Yes' : 'No'}</p>
                                        </div>
                                    </div>
                                )}
                                <div className="tw-flex tw-items-center tw-gap-2.5 tw-bg-gray-50 tw-rounded-lg tw-px-3 tw-py-2 tw-flex-1">
                                    <div className={`tw-w-7 tw-h-7 tw-rounded-md tw-flex tw-items-center tw-justify-center tw-flex-shrink-0 ${status.active ? 'tw-bg-emerald-50' : 'tw-bg-red-50'}`}>
                                        <Icon icon={status.active ? 'mdi:check-circle-outline' : 'mdi:close-circle-outline'} className={`tw-text-sm ${status.active ? 'tw-text-emerald-500' : 'tw-text-red-500'}`} />
                                    </div>
                                    <div className="tw-min-w-0">
                                        <p className="tw-text-[9px] tw-text-gray-600 tw-leading-tight">Status</p>
                                        <p className={`tw-text-[11px] tw-font-semibold ${status.active ? (status.leaving ? 'tw-text-amber-600' : 'tw-text-emerald-600') : 'tw-text-red-500'}`}>{status.label}</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Tabs — GL Accounts only exist for owners (entities
                            get a GL A/C on ownership creation). Skip for
                            tenant / family. */}
                        <div className="tw-px-5 tw-border-b tw-border-gray-200">
                            <div className="tw-flex tw-gap-1">
                                {(isOwner ? ['details', 'glAccounts'] : ['details']).map(tab => (
                                    <button
                                        key={tab}
                                        onClick={() => setActiveTab(tab)}
                                        className={`tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-border-b-2 tw-transition-colors ${
                                            activeTab === tab
                                                ? 'tw-border-blue-600 tw-text-blue-600'
                                                : 'tw-border-transparent tw-text-gray-400 hover:tw-text-gray-600'
                                        }`}
                                    >
                                        {tab === 'details'
                                            ? (isOwner ? 'Owner Details' : type === 'tenant' ? 'Tenant Details' : 'Family Details')
                                            : 'GL Accounts'}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Tab Content */}
                        <div className="tw-px-5 tw-py-4">
                            {activeTab === 'details' && (
                                <div className="tw-space-y-4">
                                    {/* Personal Information */}
                                    <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3">
                                            <div className="tw-w-5 tw-h-5 tw-rounded tw-bg-blue-100 tw-flex tw-items-center tw-justify-center">
                                                <Icon icon="mdi:account-outline" className="tw-text-blue-500 tw-text-xs" />
                                            </div>
                                            <span className="tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">Personal Information</span>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-4 tw-gap-x-4 tw-gap-y-3">
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Entity Type</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{entity.entity_type === 'I' ? 'Individual' : entity.entity_type === 'C' ? 'Company' : entity.entity_type || '-'}</p>
                                            </div>
                                            {entity.entity_type === 'C' ? (
                                                <div className="tw-col-span-3">
                                                    <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Company Name</p>
                                                    <p className="tw-text-xs tw-font-medium tw-text-gray-800">{entity.company_name || '-'}</p>
                                                </div>
                                            ) : (
                                                <>
                                                    <div>
                                                        <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">First Name</p>
                                                        <p className="tw-text-xs tw-font-medium tw-text-gray-800">{entity.first_name || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Last Name</p>
                                                        <p className="tw-text-xs tw-font-medium tw-text-gray-800">{entity.last_name || '-'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Gender</p>
                                                        <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-capitalize">{entity.gender ? entity.gender.toLowerCase() : '-'}</p>
                                                    </div>
                                                    <div>
                                                        <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Date of Birth</p>
                                                        <p className="tw-text-xs tw-font-medium tw-text-gray-800">{formatDate(entity.date_of_birth)}</p>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Contact */}
                                    <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3">
                                            <div className="tw-w-5 tw-h-5 tw-rounded tw-bg-purple-100 tw-flex tw-items-center tw-justify-center">
                                                <Icon icon="mdi:phone-outline" className="tw-text-purple-500 tw-text-xs" />
                                            </div>
                                            <span className="tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">Contact</span>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-4 tw-gap-x-4 tw-gap-y-3">
                                            <div className="tw-min-w-0">
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Email</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-truncate">{entity.email || '-'}</p>
                                            </div>
                                            <div className="tw-min-w-0">
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Secondary Email</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-truncate">{entity.secondary_email || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Phone</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{entity.phone || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Secondary Phone</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{entity.secondary_phone || '-'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Tax Details */}
                                    <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3">
                                            <div className="tw-w-5 tw-h-5 tw-rounded tw-bg-indigo-100 tw-flex tw-items-center tw-justify-center">
                                                <Icon icon="mdi:card-account-details-outline" className="tw-text-indigo-500 tw-text-xs" />
                                            </div>
                                            <span className="tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">Tax Details</span>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-4 tw-gap-x-4 tw-gap-y-3">
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">PAN No.</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-font-mono">{entity.pan_no || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">GSTIN</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-font-mono">{entity.gstin_no || '-'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Addresses */}
                                    <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3">
                                            <div className="tw-w-5 tw-h-5 tw-rounded tw-bg-green-100 tw-flex tw-items-center tw-justify-center">
                                                <Icon icon="mdi:map-marker-outline" className="tw-text-green-500 tw-text-xs" />
                                            </div>
                                            <span className="tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">Addresses</span>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-3 tw-gap-x-4 tw-gap-y-3">
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Primary Address</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-whitespace-pre-line">{entity.address || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Billing Address</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-whitespace-pre-line">{entity.billing_address || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Shipping Address</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-whitespace-pre-line">{entity.shipping_address || '-'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Unit Details */}
                                    <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3">
                                            <div className="tw-w-5 tw-h-5 tw-rounded tw-bg-orange-100 tw-flex tw-items-center tw-justify-center">
                                                <Icon icon="mdi:home-outline" className="tw-text-orange-500 tw-text-xs" />
                                            </div>
                                            <span className="tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">Unit Details</span>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-4 tw-gap-x-4 tw-gap-y-3">
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Society</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{society.name || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Society Code</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{society.society_code || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Wing</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{wing.title || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Flat No.</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{flat.flat_number || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Floor</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{flat.floor_number ?? '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Flat Type</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{flat.flat_type || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Property Type</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-capitalize">{flat.property_type ? flat.property_type.toLowerCase() : '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Facing</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-capitalize">{flat.facing ? flat.facing.toLowerCase() : '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Carpet Area</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{flat.carpet_area ? `${flat.carpet_area} sqft` : '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Built-up Area</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{flat.built_up_area ? `${flat.built_up_area} sqft` : '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Super Built-up</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{flat.super_built_up_area ? `${flat.super_built_up_area} sqft` : '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Parking</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{flat.is_parking_allotted ? `${flat.parking_slot_count || 0} slot(s)` : 'None'}</p>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Ownership / Tenancy */}
                                    {isOwner && (
                                    <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3">
                                            <div className="tw-w-5 tw-h-5 tw-rounded tw-bg-amber-100 tw-flex tw-items-center tw-justify-center">
                                                <Icon icon="mdi:file-document-outline" className="tw-text-amber-500 tw-text-xs" />
                                            </div>
                                            <span className="tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">Ownership</span>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-4 tw-gap-x-4 tw-gap-y-3">
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Ownership Share</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{details.ownership_share ? `${details.ownership_share}%` : '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Primary Owner</p>
                                                <span className={`tw-text-[10px] tw-font-medium tw-px-1.5 tw-py-0.5 tw-rounded-full ${entity.is_primary ? 'tw-bg-blue-50 tw-text-blue-600' : 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                    {entity.is_primary ? 'Primary' : 'Non-Primary'}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Status</p>
                                                <span className={`tw-text-[10px] tw-font-medium tw-px-1.5 tw-py-0.5 tw-rounded-full ${details.is_active ? STATUS_STYLES.A.className : STATUS_STYLES.I.className}`}>
                                                    {details.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Start Date</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{formatDate(details.start_date)}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">End Date</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{formatDate(details.end_date)}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Date of Admission</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{formatDate(flat.date_of_admission)}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Share Certificates</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{flat.share_certificate_count ?? '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Share Cert. No.</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-font-mono">{flat.share_certificate_number || '-'}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Sale Deed No.</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800 tw-font-mono">{flat.sale_deed_number || '-'}</p>
                                            </div>
                                        </div>
                                    </div>
                                    )}

                                    {/* Tenancy — start/end dates + resident flag for tenants and family. */}
                                    {!isOwner && (
                                    <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3">
                                            <div className="tw-w-5 tw-h-5 tw-rounded tw-bg-amber-100 tw-flex tw-items-center tw-justify-center">
                                                <Icon icon="mdi:file-document-outline" className="tw-text-amber-500 tw-text-xs" />
                                            </div>
                                            <span className="tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">
                                                {type === 'tenant' ? 'Tenancy' : 'Membership'}
                                            </span>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-4 tw-gap-x-4 tw-gap-y-3">
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Start Date</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{formatDate(details.start_date)}</p>
                                            </div>
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">End Date</p>
                                                <p className="tw-text-xs tw-font-medium tw-text-gray-800">{formatDate(details.end_date)}</p>
                                            </div>
                                            {type === 'tenant' && (
                                                <div>
                                                    <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Agreement End</p>
                                                    <p className="tw-text-xs tw-font-medium tw-text-gray-800">{formatDate(details.agreement_end_date)}</p>
                                                </div>
                                            )}
                                            <div>
                                                <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">Resident</p>
                                                <span className={`tw-text-[10px] tw-font-medium tw-px-1.5 tw-py-0.5 tw-rounded-full ${details.is_resident ? 'tw-bg-emerald-50 tw-text-emerald-600' : 'tw-bg-gray-100 tw-text-gray-600'}`}>
                                                    {details.is_resident ? 'Yes' : 'No'}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                    )}

                                    {/* Documents uploaded on Add/Edit Tenant (Police NOC, agreement). */}
                                    {type === 'tenant' && (
                                    <div className="tw-bg-gray-50 tw-rounded-lg tw-p-4">
                                        <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-3">
                                            <div className="tw-w-5 tw-h-5 tw-rounded tw-bg-blue-100 tw-flex tw-items-center tw-justify-center">
                                                <Icon icon="mdi:paperclip" className="tw-text-blue-500 tw-text-xs" />
                                            </div>
                                            <span className="tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wide">Documents</span>
                                        </div>
                                        <div className="tw-grid tw-grid-cols-2 tw-gap-x-4 tw-gap-y-3">
                                            {[...TENANT_DOCUMENT_SLOTS, { key: 'OTHER', label: 'Other', icon: 'mdi:file-outline' }].map((slot) => {
                                                const docs = (details.documents || []).filter(d => d.document_type === slot.key);
                                                if (slot.key === 'OTHER' && docs.length === 0) return null;
                                                return (
                                                    <div key={slot.key}>
                                                        <p className="tw-text-[10px] tw-text-gray-600 tw-mb-0.5">{slot.label}</p>
                                                        {docs.length === 0 ? (
                                                            <p className="tw-text-xs tw-text-gray-400">Not uploaded</p>
                                                        ) : docs.map(doc => (
                                                            <a
                                                                key={doc.document_id}
                                                                href={absoluteFileUrl(doc.file_path)}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="tw-flex tw-items-center tw-gap-1 tw-text-xs tw-font-medium tw-text-blue-600 hover:tw-underline tw-truncate"
                                                                title={doc.file_name}
                                                            >
                                                                <Icon icon={slot.icon} className="tw-text-sm tw-flex-shrink-0" />
                                                                <span className="tw-truncate">{doc.file_name}</span>
                                                            </a>
                                                        ))}
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                    )}
                                </div>
                            )}

                            {activeTab === 'glAccounts' && (
                                <div>
                                    <h4 className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-mb-3">Linked GL Accounts</h4>
                                    {glAccounts.length > 0 ? (
                                        <div className="tw-border tw-border-gray-100 tw-rounded-lg tw-overflow-hidden">
                                            <table className="tw-w-full tw-text-xs">
                                                <thead>
                                                    <tr className="tw-bg-gray-50">
                                                        <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-semibold tw-text-gray-400 tw-uppercase">A/C Code</th>
                                                        <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-semibold tw-text-gray-400 tw-uppercase">Account Name</th>
                                                        <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-semibold tw-text-gray-400 tw-uppercase">Type</th>
                                                        <th className="tw-text-left tw-px-3 tw-py-2 tw-text-[10px] tw-font-semibold tw-text-gray-400 tw-uppercase">Status</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {glAccounts.map((acc) => {
                                                        const statusStyle = STATUS_STYLES[acc.status] || { label: acc.status, className: 'tw-bg-gray-50 tw-text-gray-500' };
                                                        return (
                                                            <tr key={acc.id} className="tw-border-t tw-border-gray-50 hover:tw-bg-gray-50/50">
                                                                <td className="tw-px-3 tw-py-2.5 tw-text-gray-700 tw-font-mono tw-whitespace-nowrap">{acc.ac_code}</td>
                                                                <td className="tw-px-3 tw-py-2.5 tw-text-gray-700">{acc.name}</td>
                                                                <td className="tw-px-3 tw-py-2.5 tw-text-gray-500 tw-capitalize">{acc.type}</td>
                                                                <td className="tw-px-3 tw-py-2.5">
                                                                    <span className={`tw-text-[10px] tw-font-medium tw-px-1.5 tw-py-0.5 tw-rounded-full ${statusStyle.className}`}>
                                                                        {statusStyle.label}
                                                                    </span>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    ) : (
                                        <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10">
                                            <Icon icon="mdi:book-outline" className="tw-text-4xl tw-text-gray-200" />
                                            <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No GL accounts linked</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default ViewOwnerModal;
